/**
 * OIDC utilities for login-ui
 * Wraps @agion/oidc-client for login page specific functionality
 */

import type { UserInfo } from '@agion/oidc-client'

// Configuration
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL as string
const OIDC_ISSUER = import.meta.env.VITE_OIDC_ISSUER as string

function requireAuthServiceUrl(): string {
  if (!AUTH_SERVICE_URL || AUTH_SERVICE_URL === 'not-configured') {
    throw new Error(
      '[login-ui] VITE_AUTH_SERVICE_URL is not set. ' +
      'Add it to .env.local (e.g. VITE_AUTH_SERVICE_URL=https://auth.test.agion.dev) for local dev.'
    )
  }
  return AUTH_SERVICE_URL
}

/**
 * Check if user has active session.
 *
 * Does NOT use isAuthenticated() from @agion/oidc-client — that function
 * calls a relative /auth/session URL which nginx on login.agion.cloud serves
 * as index.html (SPA fallback), causing response.ok to be true regardless of
 * session state. We fetch the absolute auth-service URL and check the JSON
 * body's authenticated field directly.
 */
export async function checkSession(): Promise<boolean> {
  try {
    const response = await fetch(`${requireAuthServiceUrl()}/session`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) return false
    const data = await response.json() as { authenticated?: boolean }
    return data.authenticated === true
  } catch {
    return false
  }
}

/**
 * Get current authenticated user.
 * Uses absolute auth-service URL for the same reason as checkSession().
 */
export async function getSessionUser(): Promise<UserInfo | null> {
  try {
    const response = await fetch(`${requireAuthServiceUrl()}/session`, {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) return null
    const data = await response.json() as { authenticated?: boolean; user?: UserInfo }
    if (!data.authenticated) return null
    return data.user ?? null
  } catch {
    return null
  }
}

/**
 * Parse authorization request parameters from URL
 */
export interface AuthorizationRequest {
  client_id: string
  redirect_uri: string
  response_type: string
  scope: string
  state: string
  code_challenge?: string
  code_challenge_method?: string
  prompt?: string
  max_age?: number
}

export function parseAuthorizationRequest(searchParams: URLSearchParams): AuthorizationRequest | null {
  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')
  const responseType = searchParams.get('response_type')
  const scope = searchParams.get('scope')
  const state = searchParams.get('state')

  // Required parameters
  if (!clientId || !redirectUri || !responseType || !scope || !state) {
    return null
  }

  return {
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: responseType,
    scope,
    state,
    code_challenge: searchParams.get('code_challenge') || undefined,
    code_challenge_method: searchParams.get('code_challenge_method') || undefined,
    prompt: searchParams.get('prompt') || undefined,
    max_age: searchParams.get('max_age') ? parseInt(searchParams.get('max_age')!, 10) : undefined
  }
}

/**
 * Get friendly app name from client ID
 */
export function getAppName(clientId: string): string {
  const appNames: Record<string, string> = {
    'admin-ui': 'Admin Console',
    'chat-ui': 'Chat Interface',
    'mission-designer': 'Mission Designer',
    'fleet-manager': 'Fleet Manager'
  }

  return appNames[clientId] || clientId
}

/**
 * Parse scope string into array of scope names
 */
export function parseScopes(scope: string): string[] {
  return scope.split(' ').filter(Boolean)
}

/**
 * Get friendly scope descriptions
 */
export function getScopeDescriptions(scopes: string[]): Array<{ scope: string; description: string }> {
  const descriptions: Record<string, string> = {
    'openid': 'Basic authentication',
    'profile': 'Access your profile information (name, avatar)',
    'email': 'Access your email address',
    'admin': 'Administrative access to platform settings',
    'chat': 'Send and receive chat messages',
    'missions': 'Create and manage missions',
    'agents': 'Manage AI agents',
    'analytics': 'View analytics and reports',
    'offline_access': 'Access while you are offline'
  }

  return scopes.map(scope => ({
    scope,
    description: descriptions[scope] || `Access ${scope} resources`
  }))
}

/**
 * Validate redirect URI against allowed patterns
 */
export function isValidRedirectUri(redirectUri: string, clientId: string): boolean {
  try {
    const url = new URL(redirectUri)

    // Only allow HTTPS in production
    if (import.meta.env.PROD && url.protocol !== 'https:') {
      return false
    }

    // Allowed redirect patterns per client
    const allowedPatterns: Record<string, RegExp[]> = {
      'admin-ui': [
        /^https:\/\/admin\.agion\.dev\/callback$/,
        /^http:\/\/localhost:\d+\/callback$/
      ],
      'chat-ui': [
        /^https:\/\/app\.agion\.dev\/callback$/,
        /^http:\/\/localhost:\d+\/callback$/
      ],
      'mission-designer': [
        /^https:\/\/missions\.agion\.dev\/callback$/,
        /^http:\/\/localhost:\d+\/callback$/
      ]
    }

    const patterns = allowedPatterns[clientId] || []
    return patterns.some(pattern => pattern.test(redirectUri))
  } catch {
    return false
  }
}

/**
 * Build authorization response redirect URL
 */
export function buildAuthorizationResponse(
  redirectUri: string,
  code: string,
  state: string
): string {
  const url = new URL(redirectUri)
  url.searchParams.set('code', code)
  url.searchParams.set('state', state)
  return url.toString()
}

/**
 * Build error response redirect URL
 */
export function buildErrorResponse(
  redirectUri: string,
  error: string,
  errorDescription?: string,
  state?: string
): string {
  const url = new URL(redirectUri)
  url.searchParams.set('error', error)

  if (errorDescription) {
    url.searchParams.set('error_description', errorDescription)
  }

  if (state) {
    url.searchParams.set('state', state)
  }

  return url.toString()
}

/**
 * Store authorization request for later use
 */
export function storeAuthorizationRequest(request: AuthorizationRequest): void {
  sessionStorage.setItem('oidc_auth_request', JSON.stringify(request))
}

/**
 * Retrieve stored authorization request
 */
export function retrieveAuthorizationRequest(): AuthorizationRequest | null {
  const stored = sessionStorage.getItem('oidc_auth_request')
  if (!stored) return null

  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

/**
 * Clear stored authorization request
 */
export function clearAuthorizationRequest(): void {
  sessionStorage.removeItem('oidc_auth_request')
}

export { AUTH_SERVICE_URL, OIDC_ISSUER }
